import React, { useState } from 'react';
import EmergencySplash from './EmergencySplash';
import GoalsPurpose from './GoalsPurpose';
import SceneSafety from './SceneSafety';
import Demographics from './Demographics';
import PrimarySurvey from './PrimarySurvey';
import InhalationInjury from './InhalationInjury';
import MechanismAssessment from './MechanismAssessment';
import FrostbiteAssessment from './FrostbiteAssessment';
import ChemicalBurns from './ChemicalBurns';
import ElectricalBurns from './ElectricalBurns';
import SecondarySurvey from './SecondarySurvey';
import TBSAAssessment from './TBSAAssessment';
import ParklandFormula from './ParklandFormula';
import WoundCareTransfer from './WoundCareTransfer';
import AssessmentComplete from './AssessmentComplete';
// Import additional screens as we create them

const EMERGENCY_PHASES = {
  SPLASH: 'splash',
  GOALS: 'goals',
  SCENE_SAFETY: 'scene_safety',
  DEMOGRAPHICS: 'demographics',
  PRIMARY_SURVEY: 'primary_survey',
  INHALATION: 'inhalation',
  SCENE_EVAL: 'scene_eval',
  MECHANISM: 'mechanism',
  FROSTBITE: 'frostbite',
  CHEMICAL: 'chemical',
  ELECTRICAL: 'electrical',
  SECONDARY_SURVEY: 'secondary_survey',
  TBSA_ASSESSMENT: 'tbsa_assessment',
  FLUID_MANAGEMENT: 'fluid_management',
  WOUND_CARE: 'wound_care',
  COMPLETE: 'complete'
};

const EmergencyWorkflowContainer = ({ onBack }) => {
  const [currentPhase, setCurrentPhase] = useState(EMERGENCY_PHASES.SPLASH);
  const [navigationStack, setNavigationStack] = useState([]); // For tracking sub-path navigation
  const [emergencyData, setEmergencyData] = useState({
    // Store all emergency assessment data here
    scene_safety: {},
    demographics: {},
    patient_info: {},
    primary_survey: {},
    inhalation_injury: {},
    mechanism_assessment: {},
    frostbite_assessment: {},
    chemical_burns: {},
    electrical_burns: {},
    secondary_survey_ample: {},
    secondary_survey_physical: {},
    tbsa_assessment: {},
    fluid_management: {},
    wound_care: {},
    uploaded_images: [] // Store uploaded burn photos
  });

  const handlePhaseChange = (newPhase, data = {}) => {
    if (data && Object.keys(data).length > 0) {
      // Merge data into emergencyData using the keys from the data object
      setEmergencyData(prev => ({
        ...prev,
        ...data
      }));
    }
    setCurrentPhase(newPhase);
  };

  const handleSubPath = (subPath) => {
    // Save current phase to navigation stack
    setNavigationStack(prev => [...prev, currentPhase]);
    
    // Navigate to sub-path
    if (subPath === 'inhalation') {
      setCurrentPhase(EMERGENCY_PHASES.INHALATION);
    } else if (subPath === 'sceneEval') {
      setCurrentPhase(EMERGENCY_PHASES.SCENE_EVAL);
    }
  };

  const handleReturnFromSubPath = () => {
    // Pop from navigation stack and return to previous screen
    if (navigationStack.length > 0) {
      const previousPhase = navigationStack[navigationStack.length - 1];
      setNavigationStack(prev => prev.slice(0, -1));
      setCurrentPhase(previousPhase);
    }
  };

  const handleMechanismSubPath = (type) => {
    setNavigationStack(prev => [...prev, currentPhase]);
    if (type === 'frostbite') {
      setCurrentPhase(EMERGENCY_PHASES.FROSTBITE);
    } else if (type === 'chemical') {
      setCurrentPhase(EMERGENCY_PHASES.CHEMICAL);
    } else if (type === 'electrical') {
      setCurrentPhase(EMERGENCY_PHASES.ELECTRICAL);
    }
  };

  const handleDataChange = (phaseData) => {
    // Merge the phase data directly into emergencyData
    // This allows components to use specific keys like 'tbsa_assessment', 'secondary_survey_ample', etc.
    setEmergencyData(prev => ({
      ...prev,
      ...phaseData
    }));
  };

  const handleImageUpload = (images) => {
    setEmergencyData(prev => ({
      ...prev,
      uploaded_images: [...prev.uploaded_images, ...images]
    }));
  };

  const getNextPhase = () => {
    const phases = Object.values(EMERGENCY_PHASES);
    const currentIndex = phases.indexOf(currentPhase);
    return currentIndex < phases.length - 1 ? phases[currentIndex + 1] : null;
  };

  const getPrevPhase = () => {
    const phases = Object.values(EMERGENCY_PHASES);
    const currentIndex = phases.indexOf(currentPhase);
    return currentIndex > 0 ? phases[currentIndex - 1] : null;
  };

  const handleNext = () => {
    const nextPhase = getNextPhase();
    if (nextPhase) {
      handlePhaseChange(nextPhase);
    } else {
      // Complete emergency assessment - could transition to comprehensive mode
      console.log('Emergency assessment complete:', emergencyData);
    }
  };

  const handleBack = () => {
    const prevPhase = getPrevPhase();
    if (prevPhase) {
      setCurrentPhase(prevPhase);
    } else {
      // Return to mode selection
      onBack();
    }
  };

  // Render current phase
  const renderCurrentPhase = () => {
    switch (currentPhase) {
      case EMERGENCY_PHASES.SPLASH:
        return (
          <EmergencySplash 
            onNext={() => handlePhaseChange(EMERGENCY_PHASES.GOALS)} 
          />
        );
      
      case EMERGENCY_PHASES.GOALS:
        return (
          <GoalsPurpose 
            onNext={() => handlePhaseChange(EMERGENCY_PHASES.SCENE_SAFETY)}
            onBack={handleBack}
          />
        );
      
      case EMERGENCY_PHASES.SCENE_SAFETY:
        return (
          <SceneSafety
            onNext={() => handlePhaseChange(EMERGENCY_PHASES.DEMOGRAPHICS)}
            onBack={handleBack}
            onDataChange={handleDataChange}
            data={emergencyData.scene_safety || {}}
          />
        );
      
      case EMERGENCY_PHASES.DEMOGRAPHICS:
        return (
          <Demographics
            onNext={() => handlePhaseChange(EMERGENCY_PHASES.PRIMARY_SURVEY)}
            onBack={handleBack}
            onDataChange={handleDataChange}
            data={emergencyData.demographics || {}}
          />
        );
      
      case EMERGENCY_PHASES.PRIMARY_SURVEY:
        return (
          <PrimarySurvey
            onNext={() => handlePhaseChange(EMERGENCY_PHASES.SECONDARY_SURVEY)}
            onBack={handleBack}
            onSubPath={handleSubPath}
            data={emergencyData.primary_survey}
            onDataChange={handleDataChange}
          />
        );
      
      case EMERGENCY_PHASES.INHALATION:
        return (
          <InhalationInjury
            onNext={handleReturnFromSubPath}
            onBack={handleReturnFromSubPath}
          />
        );
      
      case EMERGENCY_PHASES.SCENE_EVAL:
        return (
          <SceneSafety
            onNext={() => handlePhaseChange(EMERGENCY_PHASES.MECHANISM)}
            onBack={handleReturnFromSubPath}
          />
        );
      
      case EMERGENCY_PHASES.MECHANISM:
        return (
          <MechanismAssessment
            onNext={handleReturnFromSubPath}
            onBack={handleReturnFromSubPath}
            onFrostbite={() => handleMechanismSubPath('frostbite')}
            onChemical={() => handleMechanismSubPath('chemical')}
            onElectrical={() => handleMechanismSubPath('electrical')}
            data={emergencyData.mechanism_assessment}
            onDataChange={handleDataChange}
          />
        );
      
      case EMERGENCY_PHASES.FROSTBITE:
        return (
          <FrostbiteAssessment
            onNext={handleReturnFromSubPath}
            onBack={handleReturnFromSubPath}
            onDataChange={handleDataChange}
            data={emergencyData.frostbite_assessment || {}}
          />
        );
      
      case EMERGENCY_PHASES.CHEMICAL:
        return (
          <ChemicalBurns
            onNext={handleReturnFromSubPath}
            onBack={handleReturnFromSubPath}
          />
        );
      
      case EMERGENCY_PHASES.ELECTRICAL:
        return (
          <ElectricalBurns
            onNext={handleReturnFromSubPath}
            onBack={handleReturnFromSubPath}
          />
        );
      
      case EMERGENCY_PHASES.SECONDARY_SURVEY:
        return (
          <SecondarySurvey
            onNext={() => handlePhaseChange(EMERGENCY_PHASES.FLUID_MANAGEMENT)}
            onBack={handleBack}
            onLocalBurnAssessment={() => {
              setNavigationStack(prev => [...prev, currentPhase]);
              setCurrentPhase(EMERGENCY_PHASES.TBSA_ASSESSMENT);
            }}
            onDataChange={handleDataChange}
            data={{
              ample: emergencyData.secondary_survey_ample || {},
              physical: emergencyData.secondary_survey_physical || {}
            }}
          />
        );
      
      case EMERGENCY_PHASES.TBSA_ASSESSMENT:
        return (
          <TBSAAssessment
            onNext={handleReturnFromSubPath}
            onBack={handleReturnFromSubPath}
            onDataChange={handleDataChange}
            onImageUpload={handleImageUpload}
            data={emergencyData.tbsa_assessment || {}}
            uploadedImages={emergencyData.uploaded_images || []}
          />
        );
      
      case EMERGENCY_PHASES.FLUID_MANAGEMENT:
        return (
          <ParklandFormula
            onNext={() => handlePhaseChange(EMERGENCY_PHASES.WOUND_CARE)}
            onBack={handleBack}
            onDataChange={handleDataChange}
            tbsaValue={emergencyData.tbsa_assessment?.tbsa || ''}
            patientWeight={emergencyData.demographics?.weight || emergencyData.patient_info?.weight || ''}
            data={emergencyData.fluid_management || {}}
          />
        );
      
      case EMERGENCY_PHASES.WOUND_CARE:
        return (
          <WoundCareTransfer
            onNext={() => handlePhaseChange(EMERGENCY_PHASES.COMPLETE)}
            onBack={handleBack}
            onDataChange={handleDataChange}
            data={emergencyData.wound_care || {}}
          />
        );
      
      case EMERGENCY_PHASES.COMPLETE:
        return (
          <AssessmentComplete
            onMedicalConsultation={() => {
              // Navigate to Medical Consultation mode
              onBack(); // Go back to mode selection, user can then choose Medical Consultation
            }}
            onBack={onBack}
            emergencyData={emergencyData}
          />
        );
      
      // Add other phases as we create the components
      default:
        return (
          <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900">
            <div className="text-center">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                {currentPhase.replace('_', ' ').toUpperCase()} - Coming Soon
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                This emergency assessment phase is being developed.
              </p>
              <div className="space-x-4">
                <button 
                  onClick={handleBack}
                  className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                >
                  Back
                </button>
                <button 
                  onClick={handleNext}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="emergency-workflow">
      {renderCurrentPhase()}
    </div>
  );
};

export default EmergencyWorkflowContainer;